import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Badge, Table, Alert, Spinner, ButtonGroup } from 'react-bootstrap';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaFilePdf, FaPrint, FaDownload } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

// Import score calculation utility and PDF generator
import { calculateSeminarScore } from '../../utils/seminarUtils';
import generateSeminarDocument from '../../utils/pdfGenerator';

// Tidak perlu lagi fungsi ini karena kita menggunakan nilai dari state totalSum yang dihitung 
// menggunakan calculateSeminarScore standar

// Function to determine if current user can view detailed scores
const canViewDetailedScores = (userRole, userId, seminar) => {
  if (!userRole || !userId || !seminar) return false;

  // Admin and PJ can always see details
  if (userRole === 'admin' || userRole === 'pj') return true;

  // Pembimbing 1 can see details
  if (seminar.tugasAkhir?.pembimbingId &&
    Array.isArray(seminar.tugasAkhir.pembimbingId) &&
    seminar.tugasAkhir.pembimbingId[0] === userId) {
    return true;
  }

  // Others (pembimbing 2, penguji) cannot see details
  return false;
};

const SeminarDetail = ({ seminar, show, onHide }) => {
  const { currentUser, userRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tugasAkhir, setTugasAkhir] = useState(null);
  const [mahasiswa, setMahasiswa] = useState(null);
  const [evaluators, setEvaluators] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [totalSum, setTotalSum] = useState(0);

  // Make totalSum accessible via seminar object for consistent access 
  // from both inside and outside this component
  const seminarWithScore = {
    ...seminar,
    calculatedFinalScore: totalSum
  };

  useEffect(() => {
    if (seminar && show) {
      fetchAdditionalData();
    }
  }, [seminar, show]);

  useEffect(() => {
    // Gunakan fungsi utilitas standar untuk menghitung nilai
    if (seminar && seminar.nilai) {
      const result = calculateSeminarScore(seminar, tugasAkhir);
      setTotalSum(result.finalScore);
    }
  }, [seminar, tugasAkhir]);

  const fetchAdditionalData = async () => {
    try {
      setLoading(true);

      // Ambil data tugas akhir jika ada
      if (seminar.tugasAkhirId) {
        const taDoc = await getDoc(doc(db, "tugasAkhir", seminar.tugasAkhirId));
        if (taDoc.exists()) {
          const taData = taDoc.data();
          setTugasAkhir(taData);

          // Ambil data mahasiswa
          if (taData.mahasiswaId) {
            const mahasiswaDoc = await getDoc(doc(db, "users", taData.mahasiswaId));
            if (mahasiswaDoc.exists()) {
              setMahasiswa(mahasiswaDoc.data());
            }
          }
        }
      }

      // Fetch evaluator data including signatures for PDF generation
      if (seminar.pengujiId && Array.isArray(seminar.pengujiId)) {
        // Need to get the tugas akhir first to check for pembimbing assignments
        let tugasAkhirData;
        if (seminar.tugasAkhirId) {
          const taDoc = await getDoc(doc(db, "tugasAkhir", seminar.tugasAkhirId));
          if (taDoc.exists()) {
            tugasAkhirData = taDoc.data();
          }
        }

        const pembimbingIds = tugasAkhirData?.pembimbingId || [];
        const evaluatorData = [];

        for (let i = 0; i < seminar.pengujiId.length; i++) {
          const pengujiId = seminar.pengujiId[i];
          const pengujiDoc = await getDoc(doc(db, "users", pengujiId));

          if (pengujiDoc.exists()) {
            const pengujiData = pengujiDoc.data();

            // Get roles directly from the seminar setup
            let role, weight;

            // We need to determine role based on the assigned order from tugasAkhir
            const pembimbing1Id = tugasAkhirData?.pembimbingId?.[0];
            const pembimbing2Id = tugasAkhirData?.pembimbingId?.[1];

            // For non-pembimbing evaluators, we need to identify Penguji 1 and Penguji 2 correctly
            // Get all evaluator IDs that are not pembimbing
            const nonPembimbingIds = seminar.pengujiId.filter(id =>
              id !== pembimbing1Id && id !== pembimbing2Id
            );

            // In tugasAkhir: pembimbingId has pembimbing, pengujiId has penguji
            if (pengujiId === pembimbing1Id) {
              role = 'Pembimbing 1';
              weight = 0.35;
            } else if (pengujiId === pembimbing2Id) {
              role = 'Pembimbing 2';
              weight = 0.25;
            } else {
              // First non-pembimbing in the list is Penguji 1, second is Penguji 2
              if (nonPembimbingIds[0] === pengujiId) {
                role = 'Penguji 1';
                weight = 0.20;
              } else if (nonPembimbingIds[1] === pengujiId) {
                role = 'Penguji 2';
                weight = 0.20;
              } else {
                // Fallback - this shouldn't normally happen
                role = 'Penguji';
                weight = 0.20;
              }
            }

            evaluatorData.push({
              id: pengujiId,
              nama: pengujiData.nama || seminar.pengujiNama?.[i] || `Penguji ${i + 1}`,
              role: role,
              nip: pengujiData.nip || '-',
              signatureURL: pengujiData.signatureURL || null,
              weight: weight
            });
          }
        }

        // Sort evaluators by role to ensure consistent display
        evaluatorData.sort((a, b) => {
          const roleOrder = {
            'Pembimbing 1': 0,
            'Pembimbing 2': 1,
            'Penguji 1': 2,
            'Penguji 2': 3
          };
          return roleOrder[a.role] - roleOrder[b.role];
        });

        setEvaluators(evaluatorData);
      }
    } catch (error) {
      console.error("Error fetching additional data:", error);
      setError("Terjadi kesalahan saat mengambil data tambahan");
    } finally {
      setLoading(false);
    }
  };

  // Function to generate and download PDF documents
  const generateDocument = async (documentType) => {
    try {
      setGeneratingPdf(true);
      setError('');

      // Make sure we have all the data we need
      if (!seminar || !tugasAkhir) {
        setError("Data seminar atau tugas akhir tidak lengkap");
        return;
      }

      // Make sure we have evaluator data
      if (evaluators.length === 0) {
        setError("Data penguji tidak ditemukan");
        return;
      }

      // Use our seminarWithScore that has the calculated final score
      const modifiedSeminar = {
        ...seminarWithScore,
        // Add a special field that the PDF generator can use
        manualFinalScore: totalSum,
        // Add mahasiswa data if available for PDF generation
        mahasiswa: mahasiswa || seminar.mahasiswa || null
      };

      // Generate the PDF document
      const pdfBlob = await generateSeminarDocument(modifiedSeminar, tugasAkhir, evaluators, documentType);

      // Create a download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');

      // Set the filename based on document type
      let filename = '';
      if (documentType === 'berita_acara') {
        filename = `Berita_Acara_${seminar.jenis}_${mahasiswa?.nim || 'Mahasiswa'}.pdf`;
      } else {
        filename = `Form_Penilaian_${seminar.jenis}_${mahasiswa?.nim || 'Mahasiswa'}.pdf`;
      }

      link.href = url;
      link.download = filename;
      link.click();

      // Clean up
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Error generating PDF:", error);
      setError("Terjadi kesalahan saat membuat dokumen PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Detail {seminar.jenis === 'proposal' ? 'Seminar Proposal' :
            seminar.jenis === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <Alert variant="info">Memuat data...</Alert>
        ) : (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p><strong>Tanggal:</strong> {new Date(seminar.tanggal).toLocaleDateString()}</p>
                <p><strong>Waktu:</strong> {seminar.waktu}</p>
                <p><strong>Tempat:</strong> {seminar.tempat}</p>
                <p>
                  <strong>Status:</strong>{' '}
                  <Badge bg={
                    seminar.status === 'scheduled' ? 'info' :
                      seminar.status === 'completed' ? 'success' :
                        seminar.status === 'cancelled' ? 'danger' : 'secondary'
                  }>
                    {seminar.status === 'scheduled' ? 'Terjadwal' :
                      seminar.status === 'completed' ? 'Selesai' :
                        seminar.status === 'cancelled' ? 'Dibatalkan' : seminar.status}
                  </Badge>
                </p>
              </Col>
              <Col md={6}>
                <p><strong>Mahasiswa:</strong> {mahasiswa?.nama || seminar.mahasiswaNama || '-'}</p>
                <p><strong>NIM:</strong> {mahasiswa?.nim || seminar.mahasiswaNIM || '-'}</p>
                <p><strong>Judul TA:</strong> {tugasAkhir?.judul || seminar.judulTA || '-'}</p>
              </Col>
            </Row>

            <hr />

            {seminar.kriteriaPenilaian && seminar.kriteriaPenilaian.length > 0 && (
              <div className="mt-4">
                <h6>Kriteria Penilaian:</h6>
                <Table responsive striped bordered>
                  <thead>
                    <tr>
                      <th>Kriteria</th>
                      <th>Bobot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seminar.kriteriaPenilaian.map((kriteria, index) => (
                      <tr key={index}>
                        <td>{kriteria.nama}</td>
                        <td>{kriteria.bobot}%</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {seminar.nilai && Object.keys(seminar.nilai).length > 0 && (
              <div className="mt-4">
                <h6>Hasil Penilaian:</h6>

                {/* Only show detailed breakdown for admin, pj, or pembimbing 1 */}
                {canViewDetailedScores(userRole, currentUser?.uid, seminar) ? (
                  <>
                    <Table responsive striped bordered>
                      <thead>
                        <tr>
                          <th>Penguji</th>
                          {seminarWithScore.kriteriaPenilaian?.map((kriteria, index) => (
                            <th key={index}>{kriteria.nama}</th>
                          ))}
                          <th>Rata-rata</th>
                          <th>Bobot</th>
                          <th>Kontribusi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Pembimbing 1 */}
                        {(() => {
                          const pembimbing1Id = tugasAkhir?.pembimbingId?.[0] || '';

                          if (pembimbing1Id && seminar.nilai && seminar.nilai[pembimbing1Id]) {
                            const nilai = seminar.nilai[pembimbing1Id];
                            const nama = evaluators.find(e => e.id === pembimbing1Id)?.nama ||
                              tugasAkhir?.pembimbingNama?.[0] || "Pembimbing 1";
                            let total = 0;
                            let count = 0;

                            seminar.kriteriaPenilaian?.forEach(kriteria => {
                              if (kriteria?.id && nilai[kriteria.id]) {
                                total += nilai[kriteria.id] * (kriteria.bobot / 100);
                                count++;
                              }
                            });

                            const avg = count > 0 ? total : 0;
                            const kontribusi = avg * 0.35;

                            return (
                              <tr>
                                <td>
                                  <strong>{nama}</strong>
                                  <Badge bg="primary" className="ms-2">Pembimbing 1</Badge>
                                </td>
                                {seminar.kriteriaPenilaian?.map((kriteria, idx) => (
                                  <td key={idx}>{nilai && kriteria?.id ? nilai[kriteria.id] || '-' : '-'}</td>
                                ))}
                                <td>{avg.toFixed(2)}</td>
                                <td>35%</td>
                                <td>{kontribusi.toFixed(2)}</td>
                              </tr>
                            );
                          }
                          return null;
                        })()}

                        {/* Pembimbing 2 */}
                        {(() => {
                          const pembimbing2Id = tugasAkhir?.pembimbingId?.[1] || '';

                          if (pembimbing2Id && seminar.nilai && seminar.nilai[pembimbing2Id]) {
                            const nilai = seminar.nilai[pembimbing2Id];
                            const nama = evaluators.find(e => e.id === pembimbing2Id)?.nama ||
                              tugasAkhir?.pembimbingNama?.[1] || "Pembimbing 2";
                            let total = 0;
                            let count = 0;

                            seminar.kriteriaPenilaian?.forEach(kriteria => {
                              if (kriteria?.id && nilai[kriteria.id]) {
                                total += nilai[kriteria.id] * (kriteria.bobot / 100);
                                count++;
                              }
                            });

                            const avg = count > 0 ? total : 0;
                            const kontribusi = avg * 0.25;

                            return (
                              <tr>
                                <td>
                                  <strong>{nama}</strong>
                                  <Badge bg="secondary" className="ms-2">Pembimbing 2</Badge>
                                </td>
                                {seminar.kriteriaPenilaian?.map((kriteria, idx) => (
                                  <td key={idx}>{nilai && kriteria?.id ? nilai[kriteria.id] || '-' : '-'}</td>
                                ))}
                                <td>{avg.toFixed(2)}</td>
                                <td>25%</td>
                                <td>{kontribusi.toFixed(2)}</td>
                              </tr>
                            );
                          }
                          return null;
                        })()}

                        {/* Penguji 1 */}
                        {(() => {
                          const pembimbing1Id = tugasAkhir?.pembimbingId?.[0] || '';
                          const pembimbing2Id = tugasAkhir?.pembimbingId?.[1] || '';
                          const pengujiIds = Object.keys(seminar.nilai || {});
                          const otherIds = pengujiIds.filter(id =>
                            id !== pembimbing1Id && id !== pembimbing2Id
                          );

                          if (otherIds.length > 0) {
                            const pengujiId = otherIds[0];
                            const nilai = seminar.nilai[pengujiId];
                            let nama = evaluators.find(e => e.id === pengujiId)?.nama;

                            // Find name from tugas akhir if not in evaluators
                            if (!nama) {
                              const pengujiIndex = tugasAkhir?.pengujiId?.indexOf(pengujiId);
                              if (pengujiIndex >= 0 && tugasAkhir?.pengujiNama) {
                                nama = tugasAkhir.pengujiNama[pengujiIndex];
                              } else {
                                nama = "Penguji 1";
                              }
                            }

                            let total = 0;
                            let count = 0;

                            seminar.kriteriaPenilaian?.forEach(kriteria => {
                              if (kriteria?.id && nilai[kriteria.id]) {
                                total += nilai[kriteria.id] * (kriteria.bobot / 100);
                                count++;
                              }
                            });

                            const avg = count > 0 ? total : 0;
                            const kontribusi = avg * 0.20;

                            return (
                              <tr>
                                <td>
                                  <strong>{nama}</strong>
                                  <Badge bg="info" className="ms-2">Penguji 1</Badge>
                                </td>
                                {seminar.kriteriaPenilaian?.map((kriteria, idx) => (
                                  <td key={idx}>{nilai && kriteria?.id ? nilai[kriteria.id] || '-' : '-'}</td>
                                ))}
                                <td>{avg.toFixed(2)}</td>
                                <td>20%</td>
                                <td>{kontribusi.toFixed(2)}</td>
                              </tr>
                            );
                          }
                          return null;
                        })()}

                        {/* Penguji 2 */}
                        {(() => {
                          const pembimbing1Id = tugasAkhir?.pembimbingId?.[0] || '';
                          const pembimbing2Id = tugasAkhir?.pembimbingId?.[1] || '';
                          const pengujiIds = Object.keys(seminar.nilai || {});
                          const otherIds = pengujiIds.filter(id =>
                            id !== pembimbing1Id && id !== pembimbing2Id
                          );

                          if (otherIds.length > 1) {
                            const pengujiId = otherIds[1];
                            const nilai = seminar.nilai[pengujiId];
                            let nama = evaluators.find(e => e.id === pengujiId)?.nama;

                            // Find name from tugas akhir if not in evaluators
                            if (!nama) {
                              const pengujiIndex = tugasAkhir?.pengujiId?.indexOf(pengujiId);
                              if (pengujiIndex >= 0 && tugasAkhir?.pengujiNama) {
                                nama = tugasAkhir.pengujiNama[pengujiIndex];
                              } else {
                                nama = "Penguji 2";
                              }
                            }

                            let total = 0;
                            let count = 0;

                            seminar.kriteriaPenilaian?.forEach(kriteria => {
                              if (kriteria?.id && nilai[kriteria.id]) {
                                total += nilai[kriteria.id] * (kriteria.bobot / 100);
                                count++;
                              }
                            });

                            const avg = count > 0 ? total : 0;
                            const kontribusi = avg * 0.20;

                            return (
                              <tr>
                                <td>
                                  <strong>{nama}</strong>
                                  <Badge bg="dark" className="ms-2">Penguji 2</Badge>
                                </td>
                                {seminar.kriteriaPenilaian?.map((kriteria, idx) => (
                                  <td key={idx}>{nilai && kriteria?.id ? nilai[kriteria.id] || '-' : '-'}</td>
                                ))}
                                <td>{avg.toFixed(2)}</td>
                                <td>20%</td>
                                <td>{kontribusi.toFixed(2)}</td>
                              </tr>
                            );
                          }
                          return null;
                        })()}

                        <tr className="table-primary">
                          <td colSpan={2 + (seminarWithScore.kriteriaPenilaian?.length || 0) + 1}><strong>Nilai Akhir</strong></td>
                          <td colSpan="2">
                            <strong>
                              {totalSum.toFixed(2)}
                            </strong>
                            <div className="mt-1">
                              <Badge bg={totalSum >= 60 ? "success" : "danger"}>
                                {totalSum >= 60 ? "LULUS" : "TIDAK LULUS"}
                              </Badge>
                              {seminarWithScore.isReschedule &&
                                <Badge bg="warning" className="ms-2">SEMINAR ULANG</Badge>
                              }
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* Tampilkan catatan masukan dari semua penguji/pembimbing */}
                    {seminar.nilai && Object.keys(seminar.nilai).length > 0 && (() => {
                      const allCatatanMasukan = [];

                      // Collect catatan masukan from all evaluators
                      Object.keys(seminar.nilai).forEach((pengujiId) => {
                        const evaluatorNilai = seminar.nilai[pengujiId];
                        if (evaluatorNilai && evaluatorNilai.catatanMasukan &&
                          Array.isArray(evaluatorNilai.catatanMasukan) &&
                          evaluatorNilai.catatanMasukan.length > 0) {
                          const evaluator = evaluators.find(e => e.id === pengujiId);
                          const pengujiIndex = seminar.pengujiId?.indexOf(pengujiId) ?? -1;
                          const pengujiNama = evaluator?.nama ||
                            (pengujiIndex >= 0 && seminar.pengujiNama?.[pengujiIndex]) ||
                            'Penguji';

                          // Determine role
                          const pembimbing1Id = tugasAkhir?.pembimbingId?.[0];
                          const pembimbing2Id = tugasAkhir?.pembimbingId?.[1];
                          let role = '';
                          if (pengujiId === pembimbing1Id) role = 'Pembimbing 1';
                          else if (pengujiId === pembimbing2Id) role = 'Pembimbing 2';
                          else {
                            const pengujiIds = seminar.pengujiId?.filter(id =>
                              id !== pembimbing1Id && id !== pembimbing2Id
                            ) || [];
                            const pengujiIdx = pengujiIds.indexOf(pengujiId);
                            role = pengujiIdx === 0 ? 'Penguji 1' : 'Penguji 2';
                          }

                          evaluatorNilai.catatanMasukan.forEach((catatan) => {
                            if (catatan && catatan.trim() !== '') {
                              allCatatanMasukan.push({
                                nama: pengujiNama,
                                role: role,
                                catatan: catatan
                              });
                            }
                          });
                        }
                      });

                      if (allCatatanMasukan.length === 0) return null;

                      return (
                        <div className="mt-4">
                          <h6>Catatan Masukan dari Penguji/Pembimbing:</h6>
                          {allCatatanMasukan.map((item, idx) => (
                            <div key={idx} className="mb-3 border rounded p-3 bg-light">
                              <strong>{item.nama}</strong> <Badge bg="info" className="ms-2">{item.role}</Badge>
                              <div className="mt-2">
                                {item.catatan}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  /* Simplified view for pembimbing 2 and penguji */
                  <Alert variant="info">
                    <h6>Ringkasan Nilai</h6>
                    <p>
                      Nilai akhir: <strong>{totalSum.toFixed(2)}</strong>
                      <Badge className="ms-2" bg={totalSum >= 60 ? "success" : "danger"}>
                        {totalSum >= 60 ? "LULUS" : "TIDAK LULUS"}
                      </Badge>
                      {seminarWithScore.isReschedule &&
                        <Badge bg="warning" className="ms-2">SEMINAR ULANG</Badge>
                      }
                    </p>
                    <hr />
                    <p><strong>Kontribusi nilai Anda:</strong></p>
                    {(() => {
                      // Find this user's role and contribution
                      const pembimbing1Id = tugasAkhir?.pembimbingId?.[0] || '';
                      const pembimbing2Id = tugasAkhir?.pembimbingId?.[1] || '';

                      if (!currentUser) return <p>Tidak dapat menampilkan detail nilai</p>;

                      let userRole = '';
                      let weight = 0;

                      if (currentUser.uid === pembimbing1Id) {
                        userRole = 'Pembimbing 1';
                        weight = 0.35;
                      } else if (currentUser.uid === pembimbing2Id) {
                        userRole = 'Pembimbing 2';
                        weight = 0.25;
                      } else {
                        // Check if user is a penguji
                        const pembimbingIds = [pembimbing1Id, pembimbing2Id].filter(id => id);
                        const pengujiIds = Object.keys(seminar.nilai || {}).filter(id =>
                          !pembimbingIds.includes(id)
                        );

                        if (pengujiIds[0] === currentUser.uid) {
                          userRole = 'Penguji 1';
                          weight = 0.20;
                        } else if (pengujiIds[1] === currentUser.uid) {
                          userRole = 'Penguji 2';
                          weight = 0.20;
                        }
                      }

                      if (!userRole) return <p>Anda bukan penguji pada seminar ini</p>;

                      // Calculate user's contribution
                      if (!seminar.nilai || !seminar.nilai[currentUser.uid]) {
                        return <p>Anda belum memberikan nilai</p>;
                      }

                      const nilai = seminar.nilai[currentUser.uid];
                      let total = 0;
                      let count = 0;

                      seminar.kriteriaPenilaian?.forEach(kriteria => {
                        if (kriteria?.id && nilai[kriteria.id]) {
                          total += nilai[kriteria.id] * (kriteria.bobot / 100);
                          count++;
                        }
                      });

                      const avg = count > 0 ? total : 0;
                      const kontribusi = avg * weight;

                      return (
                        <Table bordered striped size="sm">
                          <thead>
                            <tr>
                              <th>Peran Anda</th>
                              <th>Nilai Rata-rata</th>
                              <th>Bobot</th>
                              <th>Kontribusi</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{userRole}</td>
                              <td>{avg.toFixed(2)}</td>
                              <td>{(weight * 100).toFixed(0)}%</td>
                              <td>{kontribusi.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </Table>
                      );
                    })()}
                  </Alert>
                )}

                <Alert variant="info">
                  <strong>Status Penilaian:</strong>
                  <ul className="mb-0">
                    {(() => {
                      // Get pembimbing and penguji IDs from tugasAkhir
                      const pembimbing1Id = tugasAkhir?.pembimbingId?.[0] || '';
                      const pembimbing2Id = tugasAkhir?.pembimbingId?.[1] || '';

                      // Get all evaluator IDs from seminar data
                      const allEvaluatorIds = seminar.pengujiId || [];

                      // Get non-pembimbing IDs (penguji)
                      const nonPembimbingIds = allEvaluatorIds.filter(id =>
                        id !== pembimbing1Id && id !== pembimbing2Id
                      );

                      // Create array in fixed order: Pembimbing 1, Pembimbing 2, Penguji 1, Penguji 2
                      const sortedIds = [];
                      if (pembimbing1Id && allEvaluatorIds.includes(pembimbing1Id)) {
                        sortedIds.push(pembimbing1Id);
                      }
                      if (pembimbing2Id && allEvaluatorIds.includes(pembimbing2Id)) {
                        sortedIds.push(pembimbing2Id);
                      }
                      if (nonPembimbingIds[0]) {
                        sortedIds.push(nonPembimbingIds[0]); // Penguji 1
                      }
                      if (nonPembimbingIds[1]) {
                        sortedIds.push(nonPembimbingIds[1]); // Penguji 2
                      }

                      // Add any remaining IDs that weren't categorized
                      allEvaluatorIds.forEach(id => {
                        if (!sortedIds.includes(id)) {
                          sortedIds.push(id);
                        }
                      });

                      return sortedIds.map((pengujiId, index) => {
                        // Determine role for this evaluator
                        let role = "";
                        if (pengujiId === pembimbing1Id) {
                          role = "Pembimbing 1";
                        } else if (pengujiId === pembimbing2Id) {
                          role = "Pembimbing 2";
                        } else if (nonPembimbingIds[0] === pengujiId) {
                          role = "Penguji 1";
                        } else if (nonPembimbingIds[1] === pengujiId) {
                          role = "Penguji 2";
                        } else {
                          role = "Penguji";
                        }

                        // Find the evaluator to get the name
                        const evaluator = evaluators.find(e => e.id === pengujiId);
                        let pengujiNama = evaluator?.nama;

                        // If not found in evaluators, try other sources
                        if (!pengujiNama) {
                          // Try to find in tugasAkhir
                          if (pengujiId === pembimbing1Id) {
                            pengujiNama = tugasAkhir?.pembimbingNama?.[0];
                          } else if (pengujiId === pembimbing2Id) {
                            pengujiNama = tugasAkhir?.pembimbingNama?.[1];
                          } else {
                            const pengujiIndex = tugasAkhir?.pengujiId?.indexOf(pengujiId);
                            if (pengujiIndex >= 0 && tugasAkhir?.pengujiNama) {
                              pengujiNama = tugasAkhir.pengujiNama[pengujiIndex];
                            }
                          }

                          // If still not found, try seminar data
                          if (!pengujiNama) {
                            const pengujiIndex = seminar.pengujiId?.indexOf(pengujiId);
                            if (pengujiIndex >= 0 && seminar.pengujiNama &&
                              pengujiIndex < seminar.pengujiNama.length) {
                              pengujiNama = seminar.pengujiNama[pengujiIndex];
                            }
                          }

                          // Final fallback
                          if (!pengujiNama) {
                            pengujiNama = "Dosen";
                          }
                        }

                        // Check if this penguji has given scores
                        const hasGivenScore = seminar.nilai &&
                          typeof seminar.nilai === 'object' &&
                          seminar.nilai[pengujiId];

                        return (
                          <li key={index}>
                            <strong>{pengujiNama}</strong> <Badge bg="secondary" className="me-2">{role}</Badge>
                            {hasGivenScore ?
                              <Badge bg="success">Sudah Memberi Nilai</Badge> :
                              <Badge bg="warning">Belum Memberi Nilai</Badge>}
                          </li>
                        );
                      });
                    })()}
                  </ul>

                  {Object.keys(seminar.nilai).length === 4 ? (
                    <div className="mt-3">
                      <p className="mb-0">
                        <strong>Semua penguji sudah memberikan penilaian.</strong>
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <p className="mb-0">
                        <em>Menunggu {4 - Object.keys(seminar.nilai).length} penguji lainnya memberikan penilaian.</em>
                      </p>
                    </div>
                  )}
                </Alert>

                <Alert variant="info">
                  <strong>Keterangan Pembobotan:</strong>
                  <ul className="mb-0">
                    <li>Pembimbing 1: 35%</li>
                    <li>Pembimbing 2: 25%</li>
                    <li>Penguji 1: 20%</li>
                    <li>Penguji 2: 20%</li>
                  </ul>
                  <p className="mt-2 mb-0">
                    <strong>Kriteria Kelulusan:</strong> Nilai minimal 60
                  </p>
                </Alert>

                {/* Tampilkan catatan masukan untuk mahasiswa dan penguji lainnya */}
                {seminar.nilai && Object.keys(seminar.nilai).length > 0 && (() => {
                  const allCatatanMasukan = [];

                  // Collect catatan masukan from all evaluators
                  Object.keys(seminar.nilai).forEach((pengujiId) => {
                    const evaluatorNilai = seminar.nilai[pengujiId];
                    if (evaluatorNilai && evaluatorNilai.catatanMasukan &&
                      Array.isArray(evaluatorNilai.catatanMasukan) &&
                      evaluatorNilai.catatanMasukan.length > 0) {
                      const evaluator = evaluators.find(e => e.id === pengujiId);
                      const pengujiIndex = seminar.pengujiId?.indexOf(pengujiId) ?? -1;
                      const pengujiNama = evaluator?.nama ||
                        (pengujiIndex >= 0 && seminar.pengujiNama?.[pengujiIndex]) ||
                        'Penguji';

                      // Determine role
                      const pembimbing1Id = tugasAkhir?.pembimbingId?.[0];
                      const pembimbing2Id = tugasAkhir?.pembimbingId?.[1];
                      let role = '';
                      if (pengujiId === pembimbing1Id) role = 'Pembimbing 1';
                      else if (pengujiId === pembimbing2Id) role = 'Pembimbing 2';
                      else {
                        const pengujiIds = seminar.pengujiId?.filter(id =>
                          id !== pembimbing1Id && id !== pembimbing2Id
                        ) || [];
                        const pengujiIdx = pengujiIds.indexOf(pengujiId);
                        role = pengujiIdx === 0 ? 'Penguji 1' : 'Penguji 2';
                      }

                      evaluatorNilai.catatanMasukan.forEach((catatan) => {
                        if (catatan && catatan.trim() !== '') {
                          allCatatanMasukan.push({
                            nama: pengujiNama,
                            role: role,
                            catatan: catatan
                          });
                        }
                      });
                    }
                  });

                  if (allCatatanMasukan.length === 0) return null;

                  return (
                    <div className="mt-4">
                      <h6>Catatan Masukan dari Penguji/Pembimbing:</h6>
                      {allCatatanMasukan.map((item, idx) => (
                        <div key={idx} className="mb-3 border rounded p-3 bg-light">
                          <strong>{item.nama}</strong> <Badge bg="info" className="ms-2">{item.role}</Badge>
                          <div className="mt-2">
                            {item.catatan}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {seminar.catatan && (
              <div className="mt-4">
                <h6>Catatan:</h6>
                <div className="border p-3 rounded bg-light">
                  {seminar.catatan}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        {/* PDF Generation buttons - only show for admin, pj, or pembimbing 1 */}
        {seminar.status === 'completed' &&
          Object.keys(seminar.nilai || {}).length === 4 &&
          canViewDetailedScores(userRole, currentUser?.uid, seminar) && (
            <div>
              <ButtonGroup>
                <Button
                  variant="primary"
                  onClick={() => generateDocument('berita_acara')}
                  disabled={generatingPdf}
                >
                  {generatingPdf ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Memproses...
                    </>
                  ) : (
                    <>
                      <FaFilePdf className="me-2" /> Berita Acara
                    </>
                  )}
                </Button>
                <Button
                  variant="info"
                  onClick={() => generateDocument('form_penilaian')}
                  disabled={generatingPdf}
                >
                  <FaDownload className="me-2" /> Form Penilaian
                </Button>
              </ButtonGroup>
            </div>
          )}

        <Button variant="secondary" onClick={onHide}>
          Tutup
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SeminarDetail;